import React, { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, X, Upload, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";
import { api, BACKEND_URL } from "../../lib/api";
import { toast } from "sonner";

const input = "w-full border border-brand-border focus:border-brand-ink outline-none px-3 py-2.5 text-sm rounded-sm bg-white";
const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const Modal = ({ open, onClose, title, children }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-sm shadow-2xl">
                <div className="sticky top-0 bg-white border-b border-brand-border px-6 py-4 flex items-center justify-between">
                    <h2 className="font-heading font-bold text-lg">{title}</h2>
                    <button onClick={onClose} className="w-9 h-9 hover:bg-brand-bg rounded-sm flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
};

const CatForm = ({ initial, onSubmit, onCancel }) => {
    const [form, setForm] = useState({ name: "", slug: "", description: "", image: "", order: 0, is_active: true, ...initial });
    const [uploading, setUploading] = useState(false);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Imagem muito grande (máx 10MB).");
            e.target.value = "";
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd);
            setForm({ ...form, image: `${BACKEND_URL}${data.url}` });
            toast.success("Imagem enviada");
        } catch (err) { toast.error(err.response?.data?.detail || "Falha no upload"); }
        setUploading(false);
        e.target.value = "";
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...form, order: parseInt(form.order) || 0 }); }} className="space-y-4">
            <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Nome</label>
                <input data-testid="cat-name" required className={input} value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value, slug: initial?.id ? form.slug : slugify(e.target.value) }); }} />
            </div>
            <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Slug</label>
                <input data-testid="cat-slug" required className={input} value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} />
            </div>
            <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Descrição</label>
                <textarea data-testid="cat-desc" rows={3} className={input + " resize-y"} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Imagem</label>
                {form.image && (
                    <div className="mb-2 relative w-32 h-32 bg-brand-bg rounded-sm overflow-hidden border border-brand-border">
                        <img src={form.image} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => setForm({ ...form, image: "" })} className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                    </div>
                )}
                <div className="flex gap-2">
                    <label className="inline-flex items-center gap-2 px-4 py-2.5 border border-brand-border hover:border-brand-ink text-sm font-semibold rounded-sm cursor-pointer">
                        <Upload className="w-4 h-4" /> {uploading ? "Enviando..." : "Upload"}
                        <input type="file" accept="image/*" className="hidden" onChange={upload} />
                    </label>
                    <input className={input + " flex-1"} placeholder="Ou cole URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Ordem</label>
                    <input data-testid="cat-order" type="number" className={input} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
                </div>
                <label className="inline-flex items-center gap-2 text-sm mt-7">
                    <input type="checkbox" data-testid="cat-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Ativa
                </label>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-brand-border">
                <button type="button" onClick={onCancel} className="px-5 py-2.5 text-sm font-semibold hover:bg-brand-bg rounded-sm">Cancelar</button>
                <button type="submit" data-testid="cat-save" className="px-6 py-2.5 bg-brand-ink text-white text-sm font-semibold rounded-sm hover:bg-brand-dark">Salvar</button>
            </div>
        </form>
    );
};

const Categories = () => {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/categories").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const save = async (data) => {
        try {
            if (editing) await api.put(`/admin/categories/${editing.id}`, data);
            else await api.post("/admin/categories", data);
            toast.success(editing ? "Atualizada" : "Criada");
            setOpen(false);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    };

    const remove = async (c) => {
        if (!window.confirm(`Excluir categoria "${c.name}"?`)) return;
        await api.delete(`/admin/categories/${c.id}`);
        toast.success("Excluída");
        load();
    };

    const reorder = async (c, delta) => {
        await api.put(`/admin/categories/${c.id}`, { order: c.order + delta });
        load();
    };

    const toggleActive = async (c) => {
        await api.put(`/admin/categories/${c.id}`, { is_active: !c.is_active });
        load();
    };

    return (
        <div data-testid="admin-categories-page" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-1">Organização</div>
                    <h1 className="font-heading font-bold text-3xl text-brand-ink">Categorias</h1>
                    <p className="text-sm text-brand-inkSoft mt-1">{items.length} categoria{items.length !== 1 ? "s" : ""} cadastrada{items.length !== 1 ? "s" : ""}.</p>
                </div>
                <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="cat-create-btn" className="inline-flex items-center gap-2 bg-brand-ink text-white hover:bg-brand-dark px-5 py-3 text-sm font-semibold rounded-sm">
                    <Plus className="w-4 h-4" /> Nova categoria
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((c) => (
                    <div key={c.id} className={`bg-white border border-brand-border rounded-sm overflow-hidden ${!c.is_active ? "opacity-60" : ""}`} data-testid={`cat-card-${c.slug}`}>
                        <div className="aspect-[3/2] bg-brand-bg overflow-hidden">
                            {c.image ? <img src={c.image} alt={c.name} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-brand-inkSoft text-xs">Sem imagem</div>}
                        </div>
                        <div className="p-4">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                    <h3 className="font-heading font-semibold text-brand-ink">{c.name}</h3>
                                    <div className="text-xs text-brand-inkSoft">/{c.slug} · ordem {c.order}</div>
                                </div>
                                <button onClick={() => toggleActive(c)} className={`text-[10px] font-bold uppercase px-2 py-1 rounded-sm ${c.is_active ? "bg-brand-success/10 text-brand-success" : "bg-brand-bg text-brand-inkSoft"}`}>
                                    {c.is_active ? "Ativa" : "Inativa"}
                                </button>
                            </div>
                            <p className="text-xs text-brand-inkSoft line-clamp-2 min-h-[2.5rem]">{c.description}</p>
                            <div className="mt-3 flex items-center justify-between">
                                <div className="inline-flex gap-1">
                                    <button onClick={() => reorder(c, -1)} className="w-8 h-8 inline-flex items-center justify-center hover:bg-brand-bg rounded-sm"><ArrowUp className="w-4 h-4" /></button>
                                    <button onClick={() => reorder(c, 1)} className="w-8 h-8 inline-flex items-center justify-center hover:bg-brand-bg rounded-sm"><ArrowDown className="w-4 h-4" /></button>
                                </div>
                                <div className="inline-flex gap-1">
                                    <button onClick={() => { setEditing(c); setOpen(true); }} data-testid={`cat-edit-${c.slug}`} className="w-8 h-8 inline-flex items-center justify-center hover:bg-brand-bg rounded-sm"><Edit2 className="w-4 h-4" /></button>
                                    <button onClick={() => remove(c)} data-testid={`cat-delete-${c.slug}`} className="w-8 h-8 inline-flex items-center justify-center hover:bg-red-50 text-brand-error rounded-sm"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar categoria" : "Nova categoria"}>
                <CatForm initial={editing || {}} onSubmit={save} onCancel={() => setOpen(false)} />
            </Modal>
        </div>
    );
};

export default Categories;
